import { useState, useRef, useEffect, useContext } from "react";
import { Box, Grid, styled, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { loginSchema } from "@/schemas/auth.schema";
import Logo from "assets/images/newLogo.png";
import Hero from "assets/images/hero.svg";
import { authOTP, login } from "@/services/auth.service";
import { setAccessToken, setRoles } from "@/redux/actions/auth-actions";
import { showToast } from "@/redux/actions/toast-actions";
import {
  markActiveSession,
  beginPending2fa,
  clearPending2fa,
} from "@/utils/auth-session";
import OtpModal from "@/components/custom/otp-modal";
import ForceChangePasswordModal from "./_components/force-change-password-modal";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { CustomButton } from "@/components/custom/custom-button";
import AuthContext from "@/contexts/JWTAuthContext";

const JWTRoot = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fafafa",
  height: "100vh",
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${Hero})`,
  backgroundColor: theme.palette.primary.light2,
  backgroundSize: "auto",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  padding: "67px 37px",
  height: "100%",
}));

const FormContainer = styled(Box)(() => ({
  background: "#fff",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "0 48px",
}));

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [otpModal, setOtpModal] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const formValuesRef = useRef(null);
  const pendingLoginResRef = useRef(null);


  // Reaching the login page means any in-flight 2FA challenge can't be resumed
  // (the password isn't kept in memory across a reload), so clear a stale lock.
  // This runs once on mount, before any new login sets a fresh lock.
  useEffect(() => {
    clearPending2fa();
  }, []);

  // const handleLoginSuccess = (res) => {
  //   const { token, refreshToken } = res.data;
  //   dispatch(setAccessToken({ token, refreshToken }));

  //   dispatch(showToast(res?.message ?? "Login successful", "success"));
  //   navigate("/dashboard");
  // };

  const completeLogin = (res) => {
    dispatch(showToast(res?.message ?? "Login successful", "success"));
    navigate("/dashboard");
  };

  const handleLoginSuccess = (res) => {
    const { token, refreshToken, roles } = res.data;
    // pull roles out
    // The 2FA challenge (if any) is now resolved — release the lock BEFORE we
    // authenticate so no guard observes an authenticated-but-still-pending state.
    clearPending2fa();
    dispatch(setAccessToken({ token, refreshToken }));
    dispatch(setRoles(roles ?? [])); // ADD
    try {
      // FALLBACK (remove once Express proxy drops Max-Age on the refreshToken
      // cookie so it becomes a session cookie): mark an active session. This
      // marker lives in sessionStorage, which the browser clears on close, so
      // a reopened browser won't silently refresh. See PersistLogin.
      markActiveSession();
      localStorage.setItem("auth:roles", JSON.stringify(roles ?? [])); // ADD
    } catch {}

    // A new/flagged account must change its password before reaching the
    // dashboard. The token above is already set, so the change-password
    // request goes out authenticated; the dashboard redirect is deferred
    // until that succeeds.
    if (res?.data?.passwordChangeRequired) {
      pendingLoginResRef.current = res;
      setChangePasswordModal(true);
      return;
    }

    completeLogin(res);
  };

  const handlePasswordChanged = () => {
    setChangePasswordModal(false);
    const pendingRes = pendingLoginResRef.current;
    pendingLoginResRef.current = null;
    if (pendingRes) completeLogin(pendingRes);
  };

  const generateOtpMutation = useMutation({
    mutationFn: (email) => authOTP(email),
    onSuccess: () => {
      setOtpModal(true);
    },
    onError: () => {
      setOtpModal(true);
    },
  });

  const initialLoginMutation = useMutation({
    mutationFn: login,
    onSuccess: (res) => {
      const is2fa = res?.data?.user?.is2fa;

      if (is2fa) {
        // Lock the session as soon as the challenge is issued. Until the OTP is
        // verified this blocks the silent-refresh path (PersistLogin) and the
        // route guards, so the pre-OTP refresh cookie can't reach /dashboard.
        beginPending2fa(formValuesRef.current.email);
        generateOtpMutation.mutate(formValuesRef.current.email);
      } else {
        handleLoginSuccess(res);
      }
    },
    onError: (err) => {
      const msg = err?.message ?? "Invalid credentials. Please try again.";
      setLoginError(msg);
    },
  });
  const otpLoginMutation = useMutation({
    mutationFn: login,
    onSuccess: (res) => {
      if (!res?.status) {
        setOtpError(res?.message ?? "Verification failed. Try again.");
        return;
      }
      setOtpModal(false);
      handleLoginSuccess(res);
    },
    onError: (err) => {
      const msg = err?.message ?? "Invalid OTP. Please try again.";
      setOtpError(msg);
    },
  });

  const isInitialLoading =
    initialLoginMutation.isPending || generateOtpMutation.isPending;

  const handleLoginSubmit = (values) => {
    setLoginError("");
    formValuesRef.current = values;
    initialLoginMutation.mutate({
      emailOrPhoneNumber: values.email,
      password: values.password,
    });
  };

  const loginFormik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: loginSchema,
    onSubmit: handleLoginSubmit,
  });

  // Clear the login error as soon as the user edits either field.
  useEffect(() => {
    setLoginError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginFormik.values.email, loginFormik.values.password]);

  const handleOtpSubmit = ({ otp }) => {
    setOtpError("");
    otpLoginMutation.mutate({
      emailOrPhoneNumber: formValuesRef.current.email,
      password: formValuesRef.current.password,
      otp,
    });
  };

  const handleOtpClose = () => {
    if (otpLoginMutation.isPending) return;
    // Abandoning the challenge releases the lock so the user can start over.
    clearPending2fa();
    setOtpModal(false);
    setOtpError("");
    otpLoginMutation.reset();
  };

  return (
    <>
      <JWTRoot>
        <Grid container sx={{ height: "100vh" }}>
          <Grid item lg={6} md={6} sm={6} xs={0}>
            <ImageContainer>
              <img src={Logo} width="150px" alt="logo" />
            </ImageContainer>
          </Grid>

          <Grid item lg={6} md={6} sm={6} xs={12}>
            <FormContainer>
              <Typography variant="h4" fontWeight={600} mb={1}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enter your credentials to continue
              </Typography>

              <form onSubmit={loginFormik.handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormTextInput
                    formik={loginFormik}
                    name="email"
                    label="Email address"
                    placeholder="example@wayapaychat.com"
                    isDisabled={isInitialLoading}
                  />

                  <FormTextInput
                    formik={loginFormik}
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    type="password"
                    isDisabled={isInitialLoading}
                  />

                  {loginError && <CustomErrorMessage message={loginError} />}

                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      component="a"
                      href="/session/forgot-password"
                      color="primary"
                      sx={{ textDecoration: "none" }}
                    >
                      Forgot password?
                    </Typography>
                  </Box>

                  <CustomButton
                    type="submit"
                    disabled={isInitialLoading}
                    isLoading={isInitialLoading}
                    sx={{ width: "100%", height: 44, fontSize: "0.95rem" }}
                  >
                    {isInitialLoading ? "Signing in..." : "Sign In"}
                  </CustomButton>
                </Box>
              </form>
            </FormContainer>
          </Grid>
        </Grid>
      </JWTRoot>

      <OtpModal
        open={otpModal}
        onClose={handleOtpClose}
        onSubmit={handleOtpSubmit}
        isLoading={otpLoginMutation.isPending}
        errorMsg={otpError}
      />

      <ForceChangePasswordModal
        open={changePasswordModal}
        email={pendingLoginResRef.current?.data?.user?.email}
        onSuccess={handlePasswordChanged}
      />
    </>
  );
};

export default LoginPage;
